import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../../config';

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}`,
    'Content-Type': 'application/json',
});

const LOOKUP_COLUMNS = [
    'employee_status',
    'blood_group',
    'leave_type',
    'holiday_type',
    'bank_name',
    'sex',
    'vendor_type',
];

const OrgSettings = () => {
    const [company, setCompany] = useState({
        company_name: '',
        address: '',
        email_address: '',
        gst_number: '',
        website_url: '',
    });
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [newDept, setNewDept] = useState('');
    const [newDesig, setNewDesig] = useState('');
    const [lookupColumn, setLookupColumn] = useState('blood_group');
    const [lookups, setLookups] = useState([]);
    const [newLookup, setNewLookup] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setError('');
        try {
            const [c, d, g, l] = await Promise.all([
                fetch(`${API_URL}/company`, { headers: authHeaders() }),
                fetch(`${API_URL}/org/departments`, { headers: authHeaders() }),
                fetch(`${API_URL}/org/designations`, { headers: authHeaders() }),
                fetch(`${API_URL}/org/lookups/${lookupColumn}`, { headers: authHeaders() }),
            ]);
            if (c.ok) setCompany(await c.json());
            if (d.ok) {
                const dj = await d.json();
                setDepartments(dj.data || []);
            }
            if (g.ok) {
                const gj = await g.json();
                setDesignations(gj.data || []);
            }
            if (l.ok) {
                const lj = await l.json();
                setLookups(lj.data || []);
            }
        } catch (e) {
            setError(e.message || 'Failed to load org settings');
        }
    }, [lookupColumn]);

    useEffect(() => {
        load();
    }, [load]);

    const saveCompany = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/company`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify(company),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.error || 'Save failed');
            setMessage('Company saved');
            load();
        } catch (err) {
            setError(err.message);
        }
    };

    const addDept = async () => {
        if (!newDept.trim()) return;
        const res = await fetch(`${API_URL}/org/departments`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ name: newDept.trim() }),
        });
        if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            setError(b.error || 'Failed');
            return;
        }
        setNewDept('');
        load();
    };

    const addDesig = async () => {
        if (!newDesig.trim()) return;
        const res = await fetch(`${API_URL}/org/designations`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ name: newDesig.trim() }),
        });
        if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            setError(b.error || 'Failed');
            return;
        }
        setNewDesig('');
        load();
    };

    const addLookupValue = async () => {
        if (!newLookup.trim()) return;
        const res = await fetch(`${API_URL}/org/lookups/${lookupColumn}`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ value: newLookup.trim() }),
        });
        if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            setError(b.error || 'Failed');
            return;
        }
        setNewLookup('');
        load();
    };

    return (
        <div style={{ padding: 24, maxWidth: 960 }}>
            <h1 style={{ marginTop: 0 }}>Org Settings</h1>
            <p style={{ color: '#64748b' }}>Company profile, departments, designations, and lookup values.</p>
            {message && <div style={bannerOk}>{message}</div>}
            {error && <div style={bannerErr}>{error}</div>}

            <section style={card}>
                <h2>Company</h2>
                <form onSubmit={saveCompany} style={grid}>
                    {['company_name', 'email_address', 'gst_number', 'website_url'].map((k) => (
                        <label key={k} style={label}>
                            {k.replace(/_/g, ' ')}
                            <input
                                value={company[k] || ''}
                                onChange={(e) => setCompany({ ...company, [k]: e.target.value })}
                                required={k === 'company_name'}
                            />
                        </label>
                    ))}
                    <label style={{ ...label, gridColumn: '1 / -1' }}>
                        address
                        <textarea
                            rows={2}
                            value={company.address || ''}
                            onChange={(e) => setCompany({ ...company, address: e.target.value })}
                        />
                    </label>
                    <button type="submit" style={btn}>Save company</button>
                </form>
            </section>

            <section style={card}>
                <h2>Departments</h2>
                <ul>{departments.map((d) => <li key={d.id}>{d.name}</li>)}</ul>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="New department" />
                    <button type="button" style={btn} onClick={addDept}>Add</button>
                </div>
            </section>

            <section style={card}>
                <h2>Designations</h2>
                <ul>{designations.map((d) => <li key={d.id}>{d.name}</li>)}</ul>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={newDesig} onChange={(e) => setNewDesig(e.target.value)} placeholder="New designation" />
                    <button type="button" style={btn} onClick={addDesig}>Add</button>
                </div>
            </section>

            <section style={card}>
                <h2>Lookups</h2>
                <label style={label}>
                    Column
                    <select value={lookupColumn} onChange={(e) => setLookupColumn(e.target.value)}>
                        {LOOKUP_COLUMNS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <ul>{lookups.map((l) => <li key={l.id}>{l.value}</li>)}</ul>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={newLookup} onChange={(e) => setNewLookup(e.target.value)} placeholder="New value" />
                    <button type="button" style={btn} onClick={addLookupValue}>Add</button>
                </div>
            </section>
        </div>
    );
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const label = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#64748b' };
const btn = { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' };
const bannerOk = { background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 8, marginBottom: 12 };
const bannerErr = { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 };

export default OrgSettings;
